// Copyright 2025 Sorama B.V.

import { inject } from "aurelia-dependency-injection";
import { DialogService } from "aurelia-dialog";
import { BindingEngine, computedFrom, Disposable, PLATFORM } from "aurelia-framework";
import { Router } from "aurelia-router";
import { CAMiV64GuidanceCatalog } from "catalogs/GuidanceCatalog/CAMiV64GuidanceCatalog";
import { ICurrentlyActivatedPackage } from "controls/package-badge/PackageBadge";
import { ActivatedLicenseDTO, LicenseBundle, SellableLicense } from "datatypes/SoramaWebApi";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { CamIV64PackageLicenseManager } from "managers/CamIV64PackageLicenseManager";
import { ConfirmationModel } from "modules/dialogs/Confirmation/ConfirmationDialog";
import { ErrorDialogModel } from "modules/dialogs/Error/ErrorDialog";
import { FactoryResetModel } from "modules/dialogs/FactoryReset/FactoryResetDialog";
import { FirmwareUpdateModel } from "modules/dialogs/FirmwareUpdate/FirmwareUpdateDialog";
import { RecoverSoftwarePackagesModel } from "modules/dialogs/RecoverSoftwarePackages/RecoverSoftwarePackagesDialog";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { FirmwareRepository } from "repositories/FirmwareRepository";
import * as semver from "semver";
import { IChannelSelectionPresetService } from "services/ChannelSelection/IChannelSelectionPresetService";
import { ClientStatus, IClientService } from "services/Client/IClientService";
import { CamIv64Device, CamIV64Helpers } from "services/Hardware/CamHTTP/CamIV64Helpers";
import { CamIV64Service } from "services/Hardware/CamHTTP/CamIV64Service";
import { FirmwareInfo, ICamApi } from "services/Hardware/CamHTTP/ICamApi";
import { IHardwareDevice } from "services/Hardware/Datatypes/IHardwareDevice";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { ConnectedMeasurementDeviceSetup } from "services/Hardware/Datatypes/MeasurementDeviceSetup";
import { IHardwareService } from "services/Hardware/IHardwareService";
import { ILicenseService } from "services/License/ILicenseService";
import { ILicenseBundleService } from "services/LicenseBundle/ILicenseBundleService";
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";

@inject(
    CamIV64Service,
    BindingEngine,
    IMeasurementAcquisitionService,
    IClientService,
    Router,
    ILicenseService,
    ILicenseBundleService,
    ICamApi,
    ShellViewManager,
    FirmwareRepository,
    DialogService,
    WorkflowModeService,
    "IHardwareService<MeasurementDevice>",
    IChannelSelectionPresetService
)
export class HomeDeviceSelection {
    // Properties for CamIV64 devices
    private camIV64Devices: IHardwareDevice[] = [];
    private selectedCamIV64Id: string;
    private selectedCamIV64Type: string;
    private disposables: Disposable[] = [];
    private selectedCamIV64: CamIv64Device;

    // Properties for CAM64/CAM1K devices
    private cam64DeviceId: string;

    // Unified device list combining both discovery services
    protected allDevices: IHardwareDevice[] = [];

    // Selected CAM
    protected installedLicenses: ActivatedLicenseDTO[];
    protected devicePackages: ICurrentlyActivatedPackage[];
    protected installablePackages: LicenseBundle[];
    private licensesOnDevice: string[];
    private missingLicenses: ActivatedLicenseDTO[];
    protected isLoadingFirmware = true;
    protected isLoadingPackages = true;
    protected isWaitingForReboot = false;
    private installedVersion: FirmwareInfo;
    private remoteVersion: string;
    private pendingPackageFileSendDeviceId: string = null;

    protected corePackage: ICurrentlyActivatedPackage = {
        name: "Core",
        tier: "Core",
        isLifetime: true,
        expirationDate: undefined,
        tag: "package_core",
    };

    constructor(
        private readonly camIV64Service: CamIV64Service,
        private readonly bindingEngine: BindingEngine,
        private readonly measurementAcquisitionService: IMeasurementAcquisitionService,
        private readonly clientService: IClientService,
        private readonly router: Router,
        private readonly licenseService: ILicenseService,
        private readonly licenseBundleService: ILicenseBundleService,
        private readonly camApi: ICamApi,
        private readonly shellviewManager: ShellViewManager,
        private readonly firmwareRepository: FirmwareRepository,
        private readonly dialogService: DialogService,
        private readonly workflowModeService: WorkflowModeService,
        private readonly measurementDeviceService: IHardwareService<MeasurementDevice>,
        private readonly channelSelectionPresetService: IChannelSelectionPresetService
    ) {
        this.packageLicenseManager = new CamIV64PackageLicenseManager(
            this.licenseService,
            this.licenseBundleService,
            this.camApi,
            this.dialogService
        );
    }

    private packageLicenseManager: CamIV64PackageLicenseManager;

    @computedFrom("clientService.status")
    protected get isAcquisitionServiceConnected(): boolean {
        return this.clientService.status !== ClientStatus.NotAvailable;
    }

    @computedFrom("clientService.status")
    protected get isAcquisitionServiceUpdateAvailable(): boolean {
        return this.clientService.status !== ClientStatus.Available;
    }

    @computedFrom("camIV64Service.isRefreshing")
    protected get isCamIV64Refreshing(): boolean {
        return this.camIV64Service.isRefreshing;
    }

    @computedFrom("camIV64Service.isRefreshing", "measurementDeviceService.isRefreshing")
    protected get isDevicesRefreshing(): boolean {
        return this.camIV64Service.isRefreshing || (this.measurementDeviceService?.isRefreshing ?? false);
    }

    @computedFrom("selectedCamIV64Id", "cam64DeviceId")
    protected get selectedDeviceId(): string {
        return this.selectedCamIV64Id || this.cam64DeviceId;
    }

    @computedFrom("installedVersion", "remoteVersion")
    public get isFirmwareUpdateAvailable(): boolean {
        if (!this.installedVersion || !this.remoteVersion) {
            return false;
        }

        // Use semver if possible, otherwise fallback to string comparison
        try {
            const installedSemver = semver.clean(this.installedVersion.version);
            const remoteSemver = semver.clean(this.remoteVersion);

            if (installedSemver && remoteSemver) {
                // Always update if remote is different from installed
                return installedSemver !== remoteSemver;
            }
        } catch (error) {
            console.warn("Error comparing firmware versions with semver:", error);
        }

        // Fall back to string comparison if semver operations fail or return null
        return this.remoteVersion !== this.installedVersion.version;
    }

    @computedFrom("selectedCamIV64Id", "camIV64Service.selectedDeviceId", "cam64DeviceId")
    protected get isConnectedToSelectedDevice(): boolean {
        if (this.cam64DeviceId) return true;
        return this.selectedCamIV64Id != "" && this.selectedCamIV64Id === this.camIV64Service.selectedDeviceId;
    }

    @computedFrom("installedLicenses")
    protected get installedLicensesNoDuplicates(): ActivatedLicenseDTO[] {
        if (!this.installedLicenses) return [];

        return this.installedLicenses.filter((license, index, self) => {
            const firstIndex = self.findIndex(
                (l) => l.LicenseKind?.LicenseKindTag === license.LicenseKind?.LicenseKindTag
            );
            return firstIndex === index;
        });
    }

    @computedFrom("devicePackages")
    protected get devicePackagesNoDuplicates(): ICurrentlyActivatedPackage[] {
        if (!this.devicePackages) return [];

        return this.devicePackages.filter((pkg, index, self) => {
            const firstIndex = self.findIndex((p) => p.tag === pkg.tag);
            return firstIndex === index;
        });
    }

    @computedFrom("devicePackages")
    protected get isWebshopReferenceEnabled(): boolean {
        // Return false if no device packages have been loaded yet
        if (!this.devicePackages) return false;

        // Enable webshop reference only if none of the packages is an "ultimate" package
        return !this.devicePackages.some((x) => x.name.toLowerCase().includes("ultimate"));
    }

    public async bind(): Promise<void> {
        this.disposables.push(
            this.bindingEngine.propertyObserver(this.clientService, "status").subscribe(this.handleClientStatusChanged),
            this.bindingEngine
                .propertyObserver(this.camIV64Service, "hardware")
                .subscribe(this.handleCamIV64HardwareChanged),
            this.bindingEngine
                .propertyObserver(this.measurementDeviceService, "hardware")
                .subscribe(this.handleCam64HardwareChanged)
        );
        await this.initializeCamIV64Devices();
        this.measurementDeviceService?.refresh().catch((error) => {
            console.error("Error refreshing CAM64/CAM1K hardware", error);
        });
        this.rebuildAllDevices();
    }

    public unbind(): void {
        while (this.disposables.length) {
            const d = this.disposables.pop();
            if (d) {
                d.dispose();
            }
        }
    }

    public async refreshCamIV64Hardware(): Promise<void> {
        if (this.isCamIV64Refreshing) return;
        try {
            await this.camIV64Service.refresh();
        } catch (error) {
            console.error("Error refreshing CamiV64 hardware", error);
            this.camIV64Devices = [];
        }
    }

    public async refreshHardware(): Promise<void> {
        const promises: Promise<void>[] = [];
        if (
            this.workflowModeService.selectedWorkflowMode.canUseCAM64AndCAM1K &&
            this.measurementDeviceService &&
            !this.measurementDeviceService.isRefreshing
        ) {
            promises.push(
                this.measurementDeviceService.refresh().catch((error) => {
                    console.error("Error refreshing CAM64/CAM1K hardware", error);
                })
            );
        }
        if (!this.camIV64Service.isRefreshing) {
            promises.push(
                this.camIV64Service.refresh().catch((error) => {
                    console.error("Error refreshing CamIV64 hardware", error);
                    this.camIV64Devices = [];
                })
            );
        }
        await Promise.all(promises);
    }

    public getDeviceImage(device: IHardwareDevice): string {
        if (device instanceof CamIv64Device) {
            if (device.type === "iV64Ex") return "/resources/images/camiV64-ex.png";
            if (device.type === "iV64s") return "/resources/images/camIv64-s.png";
            return "/resources/images/camiV64.png";
        }
        const nameLower = device.name?.toLowerCase() ?? "";
        if (nameLower.includes("cam1k")) return "/resources/images/CAM1K.png";
        if (nameLower.includes("cam64")) return "/resources/images/CAM64.png";
        return "/resources/images/CAM64.png"; // Default image
    }

    public async selectDevice(device: IHardwareDevice): Promise<void> {
        if (device instanceof CamIv64Device) {
            this.cam64DeviceId = undefined;
            // Clear any active CAM64 setup before selecting an iV64
            if (this.measurementAcquisitionService.activeSetup) {
                this.measurementAcquisitionService.setActiveSetup(undefined);
            }
            await this.selectCamIV64(device);
        } else {
            // Clear iV64 selection so the device information panel closes
            this.selectedCamIV64Id = undefined;
            this.selectedCamIV64 = null;
            this.camIV64Service.selectedDeviceId = undefined;
            this.cam64DeviceId = device.id;

            // Connect to the CAM64/CAM1K via the acquisition service
            const measurementDevice = this.measurementDeviceService.hardware.find((d) => d.id === device.id);
            if (measurementDevice && !this.measurementAcquisitionService.isAcquiring?.available) {
                const channelSelection = this.channelSelectionPresetService.getDefaultPreset(
                    measurementDevice.frontEnd
                );
                const setup = ConnectedMeasurementDeviceSetup.fromDevice(measurementDevice, channelSelection);
                this.measurementAcquisitionService.setActiveSetup(setup);
            }
        }
    }

    public async selectCamIV64(device: CamIv64Device): Promise<void> {
        this.isLoadingFirmware = true;
        this.installedLicenses = [];
        this.missingLicenses = [];
        this.installedVersion = null;
        this.remoteVersion = null;
        this.isLoadingPackages = true;

        if (this.measurementAcquisitionService.isAcquiring?.available) {
            return;
        }
        this.selectedCamIV64Id = device.id;
        this.selectedCamIV64Type = device.type;
        this.camIV64Service.selectedDeviceId = device.id;
        this.loadCamIV64Device(device.id);
    }

    private navigate(): void {
        this.router.navigate(this.selectedCamIV64Type ? `/webshop?deviceType=${this.selectedCamIV64Type}` : "/webshop");
    }

    private async initializeCamIV64Devices(): Promise<void> {
        const currentStatus = this.clientService.status;

        if (currentStatus !== ClientStatus.Available) {
            try {
                await this.clientService.updateStatus();
            } catch (error) {
                console.error("Failed to update client status during initialization", error);
                this.camIV64Devices = [];
                return;
            }
        }
        if (currentStatus == ClientStatus.Available) {
            await this.refreshCamIV64Hardware();
        }
    }

    private async loadCamIV64Device(camId: string): Promise<void> {
        try {
            this.selectedCamIV64 = await this.camIV64Service.getHardwareById(camId);

            // Start loading operations independently
            this.loadInstalledLicenses();
            this.loadDeviceFirmware();
            this.loadRemoteFirmware();
        } catch (e) {
            console.error("Failed to get selected CAM's information", e);
        }
    }
    private async loadRemoteFirmware(): Promise<void> {
        try {
            if (!this.selectedCamIV64.type) {
                throw new Error("Could not determine device type for firmware update.");
            }
            this.remoteVersion = await this.firmwareRepository.getLatestVersion(this.selectedCamIV64.type);
        } catch (e) {
            this.remoteVersion = undefined;
            console.error("Could not fetch the latest firmware version: " + e.message);
        }
    }

    private async loadDeviceFirmware(): Promise<void> {
        try {
            this.isLoadingFirmware = true;
            this.installedVersion = await this.camApi.getDeviceFirmware();
        } catch (error) {
            console.error("Failed to load firmware information", error);
        } finally {
            this.isLoadingFirmware = false;
        }
    }

    private async loadInstalledLicenses(): Promise<void> {
        if (!this.selectedCamIV64) return;
        const state = await this.packageLicenseManager.loadInstalledLicenses(this.selectedCamIV64);
        this.installablePackages = state.installablePackages;
        this.devicePackages = state.devicePackages;
        this.installedLicenses = state.installedLicenses;
        this.missingLicenses = state.missingLicenses;
        this.isLoadingPackages = state.isLoadingPackages;
    }

    private createPackageFromBundle(bundle: LicenseBundle): ICurrentlyActivatedPackage {
        return this.packageLicenseManager.createPackageFromBundle(bundle);
    }

    private async clickedFeatureLicense(license: SellableLicense): Promise<void> {
        // Show an "are you sure" dialog
        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
                model: <ConfirmationModel>{
                    title: `Activate ${license.LicenseKind.LicenseKindName}`,
                    text: `Are you sure you want to activate the ${license.LicenseKind.LicenseKindName} license on this device?`,
                    confirmText: "Activate",
                    operation: () => this.activateLicense(license),
                },
                lock: true,
            })
            .whenClosed(async (result) => {
                if (result.wasCancelled) {
                    // An error occurred
                    if (result.output) {
                        await this.dialogService.open({
                            viewModel: PLATFORM.moduleName("modules/dialogs/Error/ErrorDialog"),
                            model: <ErrorDialogModel>{
                                text: result.output,
                            },
                        });
                    }
                } else {
                    // License activation successful, refresh license lists
                    await this.loadInstalledLicenses();
                }
                this.licenseService.reloadSellableFeatureLicenses();
            });
    }

    private async clickedPackageRecovery(): Promise<void> {
        const hardwareClock = await this.camApi.getHardwareClock();
        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/RecoverSoftwarePackages/RecoverSoftwarePackagesDialog"),
                model: <RecoverSoftwarePackagesModel>{
                    hardwareClock: hardwareClock,
                    missingLicenses: this.missingLicenses,
                    missingPackages: this.installablePackages,
                    device: this.selectedCamIV64,
                },
                lock: true,
            })
            .whenClosed(async (result) => {
                if (result.wasCancelled) {
                    // An error occurred
                    if (result.output) {
                        await this.dialogService.open({
                            viewModel: PLATFORM.moduleName("modules/dialogs/Error/ErrorDialog"),
                            model: <ErrorDialogModel>{
                                text: result.output,
                            },
                        });
                    }
                } else {
                    // Package recovery successful, refresh license lists
                    await this.sendPackageFileToDevice();
                    await this.loadInstalledLicenses();
                    this.licenseService.reloadSellableFeatureLicenses();
                }
            });
    }

    private async activateLicense(license: SellableLicense): Promise<void> {
        if (!this.selectedCamIV64) return;
        await this.packageLicenseManager.activateLicense(license, this.selectedCamIV64);
    }

    private rebuildAllDevices(): void {
        const cam64Devices = [...(this.measurementDeviceService?.hardware || [])];
        this.allDevices = [...cam64Devices, ...this.camIV64Devices];
    }

    private handleCam64HardwareChanged = (_newList: IHardwareDevice[] | undefined): void => {
        if (this.cam64DeviceId) {
            const stillPresent = (this.measurementDeviceService?.hardware || []).some(
                (d) => d.id === this.cam64DeviceId
            );
            if (!stillPresent) {
                this.cam64DeviceId = undefined;
            }
        }
        this.rebuildAllDevices();
    };

    private handleClientStatusChanged = (newStatus: ClientStatus, oldStatus?: ClientStatus): void => {
        if (newStatus == ClientStatus.Available) {
            this.refreshCamIV64Hardware();
        } else {
            this.camIV64Devices = [];
            this.selectedCamIV64Id = undefined;
        }
    };

    private filterHandheldDevices(devices: IHardwareDevice[]): IHardwareDevice[] {
        if (!devices || devices.length === 0) {
            return [];
        }

        const supportedCamTypes = this.workflowModeService.selectedWorkflowMode.CAMSet;

        return devices.filter((device) => {
            const deviceType = CamIV64Helpers.getDeviceType(device.id);
            if (!deviceType) return false;

            // Map device types to CAMSet names
            switch (deviceType) {
                case "iV64":
                    return supportedCamTypes.has("CAMiV64");
                case "iV64Ex":
                    return supportedCamTypes.has("CAMiV64Ex");
                case "iV64s":
                    return supportedCamTypes.has("CAMiV64s");
                default:
                    return false;
            }
        });
    }

    private handleCamIV64HardwareChanged = (newHardwareList: IHardwareDevice[] | undefined): void => {
        const allDevices = newHardwareList ? [...newHardwareList] : [];
        this.camIV64Devices = this.filterHandheldDevices(allDevices);
        this.rebuildAllDevices();
        // Make sure our selected device corresponds to that in the camiv64service
        this.selectedCamIV64Id = this.camIV64Service.selectedDeviceId;

        // If the selected device is not in the filtered list, clear selection
        const stillPresent = this.camIV64Devices.some((d) => d.id === this.selectedCamIV64Id);
        if (!stillPresent) {
            this.selectedCamIV64Id = undefined;
            this.selectedCamIV64 = null;
            return;
        }

        this.isWaitingForReboot = false;

        // If a device that needed a package file send after reboot is back online, send it now
        if (
            this.pendingPackageFileSendDeviceId &&
            allDevices.some((d) => d.id === this.pendingPackageFileSendDeviceId)
        ) {
            const deviceId = this.pendingPackageFileSendDeviceId;
            this.pendingPackageFileSendDeviceId = null;
            this.camIV64Service.getHardwareById(deviceId).then((device) => {
                if (device) {
                    this.packageLicenseManager.sendPackageFileToDevice(device);
                }
            });
        }

        // Make sure the device's properties are also loaded
        if (this.selectedCamIV64Id) {
            this.loadCamIV64Device(this.selectedCamIV64Id);
        } else {
            this.selectedCamIV64 = null;
        }
    };

    protected downloadAcquisitionService(): void {
        FileDownloader.downloadAsFileFromURL(
            "https://portal.sorama.eu/sorama%20acquisition%20service.exe",
            "Sorama Acquisition Service.exe"
        );
    }

    public async downloadLogs(): Promise<void> {
        const url = await this.camApi.getLogUrl();
        // Get extension from URL
        const extension = url.split(".").pop() || "log";
        FileDownloader.downloadAsFileFromURL(
            url,
            `${this.selectedCamIV64Id}_logs_${new Date().toISOString()}.${extension}`
        );
    }

    public async updateFirmware(): Promise<void> {
        if (!this.installedVersion || !this.remoteVersion || !this.selectedCamIV64) {
            console.error("Missing firmware or device information");
            return;
        }

        const model: FirmwareUpdateModel = {
            device: this.selectedCamIV64,
            installedVersion: this.installedVersion,
            remoteVersion: this.remoteVersion,
        };

        try {
            const result = await this.dialogService
                .open({
                    viewModel: PLATFORM.moduleName("modules/dialogs/FirmwareUpdate/FirmwareUpdateDialog"),
                    model,
                    lock: true,
                })
                .whenClosed(async (response) => {
                    if (!response.wasCancelled) {
                        // Reload firmware information after successful update
                        await this.loadDeviceFirmware();
                        await this.loadRemoteFirmware();
                        // Mark this device as needing a package file send after reboot
                        this.pendingPackageFileSendDeviceId = this.selectedCamIV64Id;
                        this.isWaitingForReboot = true;
                    }
                });
        } catch (error) {
            console.error("Firmware update failed:", error);
        }
    }

    public async performFactoryReset(): Promise<void> {
        if (!this.selectedCamIV64) {
            console.error("No CAM IV64 device selected for factory reset");
            return;
        }

        const model: FactoryResetModel = {
            device: this.selectedCamIV64,
        };

        try {
            const result = await this.dialogService
                .open({
                    viewModel: PLATFORM.moduleName("modules/dialogs/FactoryReset/FactoryResetDialog"),
                    model,
                    lock: true,
                })
                .whenClosed(async (response) => {
                    if (!response.wasCancelled) {
                        // Optionally reload device information after successful reset
                        await this.loadDeviceFirmware();
                        // Mark this device as needing a package file send after reboot
                        this.pendingPackageFileSendDeviceId = this.selectedCamIV64Id;
                        this.isWaitingForReboot = true;
                    }
                });
        } catch (error) {
            console.error("Factory reset failed:", error);
        }
    }

    private async sendPackageFileToDevice(): Promise<void> {
        if (!this.selectedCamIV64) return;
        await this.packageLicenseManager.sendPackageFileToDevice(this.selectedCamIV64);
    }

    public openHelpSection(): void {
        this.shellviewManager.openRightSideBarHelp(CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule.name);
    }

    protected async clickedPackage(installablePackage: LicenseBundle): Promise<void> {
        // Extract package name, prepend "Expert -" if it's an expert package
        const { Tier: tier = "", Name: baseName = "Unknown package" } = installablePackage.LicenseBundleKind || {};
        const name = tier.trim().toLowerCase() == "expert" ? `Expert - ${baseName}` : baseName;

        // Show an "are you sure" dialog
        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
                model: <ConfirmationModel>{
                    title: `Install package ${baseName}`,
                    text: `Are you sure you want to install the "${name}" package on your Sorama CAM ${this.selectedCamIV64.type} with serial number ${this.selectedCamIV64.serialNumber}?`,
                    confirmText: "Install",
                    operation: () => this.installPackage(installablePackage),
                },
                lock: true,
            })
            .whenClosed(async (result) => {
                if (result.wasCancelled) {
                    // An error occurred
                    if (result.output) {
                        await this.dialogService.open({
                            viewModel: PLATFORM.moduleName("modules/dialogs/Error/ErrorDialog"),
                            model: <ErrorDialogModel>{
                                text: result.output,
                            },
                        });
                    }
                } else {
                    // License activation successful, refresh license lists
                    await this.loadInstalledLicenses();
                }
                this.licenseService.reloadSellableFeatureLicenses();
            });
    }

    protected async installPackage(installablePackage: LicenseBundle): Promise<void> {
        if (!this.selectedCamIV64) return;
        await this.packageLicenseManager.installPackage(installablePackage, this.selectedCamIV64);
        await this.refreshCamIV64Hardware();
    }
}
